import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';

// @mui/material components
import { Box, Paper, Typography } from '@mui/material';

import CtaLinkRowActions from './CtaLinkRowActions';
import CtaLinkTranslationFields from './CtaLinkTranslationFields';
import OpenInNewTabSwitch from './OpenInNewTabSwitch';
import { hasOnlyHiddenLocaleContent, isCtaLinkIncomplete } from './helpers';

export default function CtaLinkRow({ ctaLink, locales, onChange, onDelete, onMoveUp, onMoveDown, canMoveUp, canMoveDown }) {
    // The "incomplete" warning waits for a first blur so it does not flash from the very
    // first typed character — except when the row only carries content in locales the
    // organization no longer has (never editable here): warn immediately, it is the only
    // visible explanation for the blocked submit.
    const [hasBlurred, setHasBlurred] = useState(false);

    const localeCodes = locales.map((locale) => locale.code);

    const changeTranslation = (localeCode, field, fieldValue) => {
        onChange({
            ...ctaLink,
            translations: {
                ...ctaLink.translations,
                [localeCode]: { ...ctaLink.translations?.[localeCode], [field]: fieldValue },
            },
        });
    };

    const incomplete = isCtaLinkIncomplete(ctaLink, localeCodes);
    const showIncompleteWarning = incomplete && (hasBlurred || hasOnlyHiddenLocaleContent(ctaLink, localeCodes));

    return (
        <Paper variant="outlined" sx={{ p: 2, mb: 1.5 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flexGrow: 1 }}>
                    {locales.map((locale) => (
                        <CtaLinkTranslationFields
                            key={locale.code}
                            locale={locale}
                            translation={ctaLink.translations?.[locale.code] ?? { text: '', url: '' }}
                            showLocaleLabel={locales.length > 1}
                            onChangeField={(field, fieldValue) => changeTranslation(locale.code, field, fieldValue)}
                            onBlur={() => setHasBlurred(true)}
                        />
                    ))}
                    <OpenInNewTabSwitch
                        checked={!!ctaLink.openInNewTab}
                        onChange={(openInNewTab) => onChange({ ...ctaLink, openInNewTab })}
                    />
                    {showIncompleteWarning && (
                        <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                            <FormattedMessage id="category.form.ctaLinks.incomplete" />
                        </Typography>
                    )}
                </Box>
                <CtaLinkRowActions
                    onMoveUp={onMoveUp}
                    onMoveDown={onMoveDown}
                    onDelete={onDelete}
                    canMoveUp={canMoveUp}
                    canMoveDown={canMoveDown}
                />
            </Box>
        </Paper>
    );
}
